import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Panel, Button, IconButton, Field, TextInput, Tag, Banner, EmptyState, Spinner, FileDrop, Dialog, Select, useCkToast, ckConfirm,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { apiError, fmtDate, Toggle } from '../../next/ui';
import { contentLessonChannels } from '../../data/lessonChannels.js';
import IntroExamsOpenedPanel from './IntroExamsOpenedPanel.jsx';
import { introUploadMessage } from './introUploadMessage.js';

const AUDIO_EXT_RE = /\.(mp3|wav|ogg|m4a|aac|flac|wma|webm)$/i;
const DEFAULT_MOVE_CHANNEL = 'shinun_blik';
const MOVE_CHANNEL_OPTIONS = contentLessonChannels()
  .filter((c) => c.id !== 'all_lessons')
  .map((c) => ({ value: c.id, label: `${c.label} (${c.ivrPath})` }));
const DEFAULT_DAILY_CHANNEL = 'daf_yomi_sikum';
const AMUD_MARK = { a: '.', b: ':' };

/** מה מושמע היום מפריט «שיעור היום» (clip.today מהשרת) */
function describeToday(today) {
  if (!today) return 'אין שיעורים פעילים בערוץ — לא יושמע דבר';
  const { lesson, page, match } = today;
  const ref = lesson.tractate && lesson.daf ? ` (${lesson.tractate} ${lesson.daf}${AMUD_MARK[lesson.amud] || ''})` : '';
  const plan = page?.text ? `לפי הלוח: ${page.text}` : 'אין דף בלוח';
  return match === 'page'
    ? `מושמע היום: «${lesson.title || 'שיעור'}»${ref} · ${plan}`
    : `אין הקלטה לדף (${plan}) — מושמע השיעור האחרון: «${lesson.title || 'שיעור'}»${ref}`;
}

/**
 * פתיח IVR — new design. Same endpoints as IvrIntroManager (/mishtameret-intro…):
 * phone message (77 → 9 → 3), uploaded summaries (newest first, upload activates the new one),
 * play / activate / delete, and syncing the management extension in Yemot.
 */
export default function IvrIntroNext() {
  const toast = useCkToast();
  const [clips, setClips] = useState([]);
  const [autoMove, setAutoMove] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [title, setTitle] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingUrl, setPendingUrl] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [moveClip, setMoveClip] = useState(null);
  const [moveForm, setMoveForm] = useState({ channel: DEFAULT_MOVE_CHANNEL, title: '' });
  const [moving, setMoving] = useState(false);
  const [dailyChannel, setDailyChannel] = useState(DEFAULT_DAILY_CHANNEL);
  const [addingDaily, setAddingDaily] = useState(false);
  const audioElRef = useRef(null);

  const stopAudio = useCallback(() => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
    setPlayingId(null);
  }, []);
  useEffect(() => () => {
    stopAudio();
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
  }, [stopAudio, pendingUrl]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/mishtameret-intro');
      setClips(data.clips || []);
      setAutoMove(data.autoMoveSummaries !== false);
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינה'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const playUrl = (id, url, revoke) => {
    const audio = new Audio(url);
    audio.onended = () => { setPlayingId(null); if (revoke) URL.revokeObjectURL(url); audioElRef.current = null; };
    audio.onerror = () => { toast.error('לא ניתן להשמיע את הקובץ'); if (revoke) URL.revokeObjectURL(url); audioElRef.current = null; setPlayingId(null); };
    audioElRef.current = audio;
    setPlayingId(id);
    audio.play().catch(() => { setPlayingId(null); });
  };

  const playClip = async (clipId) => {
    const id = `clip-${clipId}`;
    if (playingId === id && audioElRef.current) { stopAudio(); return; }
    stopAudio();
    try {
      const { data: blob, status } = await api.get(`/mishtameret-intro/clips/${clipId}/audio`, { responseType: 'blob' });
      if (blob.type?.includes('application/json') || status >= 400) {
        let text = 'שגיאה בהשמעה';
        try { text = JSON.parse(await blob.text()).error || text; } catch { /* ignore */ }
        toast.error(text);
        return;
      }
      playUrl(id, URL.createObjectURL(blob), true);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהשמעה'));
      setPlayingId(null);
    }
  };

  const playPending = () => {
    if (!pendingUrl) return;
    if (playingId === 'pending' && audioElRef.current) { stopAudio(); return; }
    stopAudio();
    playUrl('pending', pendingUrl, false);
  };

  const clearPending = () => {
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(null);
    setPendingUrl('');
    if (playingId === 'pending') stopAudio();
  };

  const onFileSelected = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('audio/') && !AUDIO_EXT_RE.test(file.name || '')) {
      toast.error('יש להעלות קובץ שמע');
      return;
    }
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(file);
    setPendingUrl(URL.createObjectURL(file));
    if (!title.trim()) setTitle(file.name.replace(AUDIO_EXT_RE, ''));
  };

  const toggleActive = async (clip) => {
    try {
      const { data } = await api.patch(`/mishtameret-intro/clips/${clip._id}`, { active: !clip.active });
      setClips((prev) => prev.map((c) => (c._id === data._id ? data : c)));
      toast.success(data.active ? 'ההקלטה הופעלה' : 'ההקלטה כובתה');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעדכון'));
    }
  };

  const removeClip = async (clip) => {
    if (!(await ckConfirm({ title: 'מחיקת הקלטה', message: `למחוק את «${clip.title || 'הקלטה'}»?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/mishtameret-intro/clips/${clip._id}`);
      setClips((prev) => prev.filter((c) => c._id !== clip._id));
      toast.success('ההקלטה נמחקה');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקה'));
    }
  };

  const openMove = (clip) => {
    setMoveForm({ channel: DEFAULT_MOVE_CHANNEL, title: clip.title || '' });
    setMoveClip(clip);
  };

  const submitMove = async () => {
    if (!moveClip) return;
    setMoving(true);
    try {
      await api.post(`/mishtameret-intro/clips/${moveClip._id}/move`, moveForm);
      if (playingId === `clip-${moveClip._id}`) stopAudio();
      setClips((prev) => prev.filter((c) => c._id !== moveClip._id));
      const label = MOVE_CHANNEL_OPTIONS.find((o) => o.value === moveForm.channel)?.label || moveForm.channel;
      toast.success(`ההקלטה הועברה ל«${label}» והוסרה מהפתיח`);
      setMoveClip(null);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהעברה'));
    } finally {
      setMoving(false);
    }
  };

  const onUpload = async () => {
    if (!pendingFile) { toast.error('בחרו קובץ להעלאה'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('audio', pendingFile);
      if (title.trim()) fd.append('title', title.trim());
      const { data } = await api.post('/mishtameret-intro/clips', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTitle('');
      clearPending();
      const result = introUploadMessage(data);
      if (result.failed) toast.error(result.text);
      else toast.success(result.text);
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהעלאה'));
    } finally {
      setUploading(false);
    }
  };

  const toggleAutoMove = async () => {
    try {
      const { data } = await api.put('/mishtameret-intro/auto-move-summaries', { enabled: !autoMove });
      setAutoMove(data.autoMoveSummaries);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעדכון'));
    }
  };

  const addDaily = async () => {
    setAddingDaily(true);
    try {
      await api.post('/mishtameret-intro/clips/channel', { channel: dailyChannel });
      toast.success('שיעור היום נוסף לפתיח');
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהוספה'));
    } finally {
      setAddingDaily(false);
    }
  };

  const ensureYemot = async () => {
    setEnsuring(true);
    try {
      await api.post('/mishtameret-intro/ensure-yemot');
      toast.success('שלוחת ניהול 77/9 עודכנה בימות');
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בחיבור לימות'));
    } finally {
      setEnsuring(false);
    }
  };

  const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
  const phoneClips = clips.filter((c) => c.source === 'phone').sort(byNewest);
  const dailyClips = clips.filter((c) => c.source === 'channel').sort(byNewest);
  const summaryClips = clips.filter((c) => c.source !== 'phone' && c.source !== 'channel').sort(byNewest);

  const renderClip = (clip) => {
    const playing = playingId === `clip-${clip._id}`;
    return (
      <div key={clip._id} className={`nx-item ${clip.active ? '' : 'is-off'}`}>
        <IconButton icon={playing ? 'pause' : 'play'} label={playing ? 'עצירה' : 'השמעה'} onClick={() => playClip(clip._id)} />
        <div className="nx-item__main">
          <b>{clip.title || 'ללא כותרת'}</b>
          {clip.source === 'channel'
            ? <small>{describeToday(clip.today)}</small>
            : <small>{fmtDate(clip.createdAt, true)} · <span className="nx-code">{clip.yemotPath}</span></small>}
        </div>
        <div className="nx-item__acts">
          <Tag tone={clip.active ? 'success' : 'neutral'}>{clip.active ? 'פעיל' : 'כבוי'}</Tag>
          <Toggle checked={!!clip.active} onChange={() => toggleActive(clip)} label={clip.active ? "כיבוי" : "הפעלה"} />
          {clip.source !== 'channel' ? <Button size="sm" variant="ghost" icon="swap" onClick={() => openMove(clip)}>מעבר</Button> : null}
          <IconButton icon="trash" label="מחיקה" onClick={() => removeClip(clip)} />
        </div>
      </div>
    );
  };

  if (loading) return <div className="ck-empty"><Spinner size={26} /></div>;

  return (
    <div className="nx-stack">
      <p className="nx-desc">
        לפני «ברוכים הבאים...» (001) מושמעת קודם הודעת הטלפון, ואחריה הפריטים הפעילים — שיעורי היום והסיכומים, מהחדש לישן.
        הודעת טלפון (77 → 9 → סיסמה → 3): הודעה אחת פעילה; בהקלטה חדשה הקודמת עוברת ל«א בליק אויפן בלעט» (ואם ההעברה האוטומטית כבויה — נמחקת).
        סיכום שמועלה מכאן מופעל אוטומטית, והסיכום הפעיל הקודם עובר ל«א בליק אויפן בלעט» (אלא אם ההעברה האוטומטית כבויה — אז הוא רק נכבה).
      </p>
      {error ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={load}>נסו שוב</Button>}>{error}</Banner> : null}

      <Panel
        title="העלאת סיכום"
        icon="upload"
        actions={<Button size="sm" variant="ghost" icon="refresh" disabled={ensuring} onClick={ensureYemot}>{ensuring ? 'מסנכרן…' : 'סנכרן שלוחת ניהול בימות'}</Button>}
      >
        <div className="nx-stack">
          <Field label="כותרת (אופציונלי)">
            <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="למשל: סיכום שבוע פרשת…" />
          </Field>
          {pendingFile ? (
            <div className="nx-item is-on">
              <IconButton icon={playingId === 'pending' ? 'pause' : 'play'} label="השמעה לפני העלאה" onClick={playPending} />
              <div className="nx-item__main"><b>{pendingFile.name}</b><small>מוכן להעלאה</small></div>
              <div className="nx-item__acts">
                <Button variant="ghost" disabled={uploading} onClick={clearPending}>ביטול</Button>
                <Button variant="primary" icon="upload" disabled={uploading} onClick={onUpload}>{uploading ? 'מעלה…' : 'העלאה והפעלה'}</Button>
              </div>
            </div>
          ) : (
            <FileDrop accept="audio/*,.mp3,.wav,.ogg,.m4a" onFile={onFileSelected} title="גררו קובץ שמע לכאן, או לחצו לבחירה" hint="mp3 · wav · ogg · m4a" icon="mic" />
          )}
        </div>
      </Panel>

      <Panel title="הודעת טלפון (9/3)" icon="phone" tone="info">
        {phoneClips.length ? <div className="nx-list">{phoneClips.map(renderClip)}</div>
          : <EmptyState compact icon="phone" title="אין הודעת טלפון">להקלטה: 77 → 9 → סיסמה → 3.</EmptyState>}
      </Panel>

      <Panel title={`שיעור היום מערוץ (${dailyClips.length})`} sub="נבחר אוטומטית לפי לוח השנה" icon="calendar" tone="success">
        <div className="nx-stack">
          <p className="nx-desc ck-muted">
            בכל שיחה נבחרת ההקלטה של הדף של היום: בערוצי דף היומי — לפי הדף היומי, בערוצי שינון — לפי עמודי השינון.
            יום בלי דף (למשל שבת, חזרה) — הדף האחרון שהיה בלוח. אין הקלטה משויכת לדף — השיעור האחרון בערוץ.
            השיוך לדף נקבע בעריכת השיעור (מסכת · דף · עמוד).
          </p>
          <div className="nx-item">
            <div className="nx-item__main">
              <Select value={dailyChannel} onChange={setDailyChannel} options={MOVE_CHANNEL_OPTIONS} />
            </div>
            <div className="nx-item__acts">
              <Button variant="primary" icon="plus" disabled={addingDaily || !dailyChannel} onClick={addDaily}>{addingDaily ? 'מוסיף…' : 'הוספה לפתיח'}</Button>
            </div>
          </div>
          {dailyClips.length ? <div className="nx-list">{dailyClips.map(renderClip)}</div> : null}
        </div>
      </Panel>

      <Panel title={`סיכומים (${summaryClips.length})`} sub="מהחדש לישן" icon="play" tone="success">
        <div className="nx-stack">
          <div className="nx-item">
            <div className="nx-item__main">
              <b>העברה אוטומטית ל«א בליק אויפן בלעט»</b>
              <small>כשעולה סיכום חדש או הודעת טלפון חדשה, הפעיל הקודם מאותו סוג עובר לערוץ ומוסר מכאן.</small>
            </div>
            <div className="nx-item__acts">
              <Toggle checked={autoMove} onChange={toggleAutoMove} label={autoMove ? 'כיבוי העברה אוטומטית' : 'הפעלת העברה אוטומטית'} />
            </div>
          </div>
          {summaryClips.length ? <div className="nx-list">{summaryClips.map(renderClip)}</div>
            : <EmptyState compact icon="play" title="אין סיכומים">העלו קובץ למעלה.</EmptyState>}
        </div>
      </Panel>

      <Dialog
        open={!!moveClip}
        onClose={() => !moving && setMoveClip(null)}
        title="העברת הקלטה לערוץ"
        icon="swap"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={moving} onClick={() => setMoveClip(null)}>ביטול</Button>
            <Button variant="primary" disabled={moving || !moveForm.channel} onClick={submitMove}>{moving ? 'מעביר…' : 'העברה'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <Field label="ערוץ יעד">
            <Select value={moveForm.channel} onChange={(v) => setMoveForm((f) => ({ ...f, channel: v }))} options={MOVE_CHANNEL_OPTIONS} />
          </Field>
          <Field label="כותרת השיעור">
            <TextInput value={moveForm.title} onChange={(e) => setMoveForm((f) => ({ ...f, title: e.target.value }))} />
          </Field>
          <p className="nx-desc ck-muted">ההקלטה תתווסף כשיעור פעיל בראש הערוץ ותוסר מהפתיח.</p>
        </div>
      </Dialog>

      <IntroExamsOpenedPanel />

      <p className="nx-desc ck-muted">אחרי הרשימה למעלה מושמע תמיד 001 («ברוכים הבאים...»), ולידו 243 («המבחנים נפתחו במערכת») לפי ההגדרות שלמעלה. לא משתמשים ב-M0001.</p>
    </div>
  );
}
